import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListFilter } from "lucide-react";
import HeroSlider from "./components/HeroSlider";
import CategoryGrid from "./components/CategoryGrid";
import ProductCard from "./components/ProductCard";
import Pagination from "./components/Pagination";
import BG1 from "../../assets/images/bg_1.png";
import BG2 from "../../assets/images/bg_2.png";
import { ReactComponent as Star } from "../../assets/images/Star.svg";
import SearchCard from "./components/SearchCard";

import SelectField from "../../components/inputs/SelectField";

import useApiClient from "../../api/client";
import config from "../../config/env";

const HERO_MODAL_SEEN_SESSION_KEY = "public_home_hero_seen";

function joinImgUrl(base, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return b && p ? `${b}/${p}` : "";
}

function resolveImageUrl(image) {
  if (!image) return "";
  const path =
    image?.formats?.medium?.url ||
    image?.formats?.small?.url ||
    image?.formats?.thumbnail?.url ||
    image?.url ||
    image?.attributes?.formats?.medium?.url ||
    image?.attributes?.formats?.small?.url ||
    image?.attributes?.formats?.thumbnail?.url ||
    image?.attributes?.url ||
    image?.data?.attributes?.formats?.medium?.url ||
    image?.data?.attributes?.formats?.small?.url ||
    image?.data?.attributes?.formats?.thumbnail?.url ||
    image?.data?.attributes?.url ||
    "";
  return joinImgUrl(config.apiImg, path);
}

function pickFirstImageUrl(product) {
  const imgs = product?.images;
  if (Array.isArray(imgs) && imgs.length > 0) {
    return resolveImageUrl(imgs[0]);
  }
  return "";
}

function mapProductToCardItem(p) {
  const productName = String(
    p?.product_name || p?.model?.category?.name || "",
  ).trim();
  const modelName = String(p?.model?.name || "").trim();
  const title = productName;
  const colorName = p?.color?.name || "-";
  const capText = p?.capacity?.size || p?.capacity?.name || "-";

  const price = Number(p?.sell_price ?? 0) || Number(p?.buy_price ?? 0) || 0;

  return {
    id: String(p?.id ?? ""),
    title,
    batteryHealth: p?.battery_health,
    usage: String(p?.usage || ""),
    color: colorName,
    capacity: capText,
    detail: String(p?.website_description || p?.description || ""),
    warranty: String(p?.warranty_info || p?.warranty_status || "-"),
    warrantyEndDate: p?.warranty_end_date || p?.warranty_date || "",
    price,
    isOnsale: Boolean(p?.is_onsale),
    onsaleDiscount: Number(p?.onsale_discount ?? 0) || 0,
    image: pickFirstImageUrl(p),
    raw: p,
  };
}

function FullscreenHeroModal({
  open,
  onClose,
  onEstimate,
  onMap,
  onEnter,
  brand = "Bestphone",
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999]">
      <div
        className="absolute inset-0"
        style={{ background: "#000000CC" }}
        onClick={onClose}
      />

      <div className="absolute inset-x-0 top-0 z-[1000]">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-white font-medium tracking-wide">{brand}</div>

            <div className="hidden md:flex items-center gap-10 text-white/90 text-sm">
              <button type="button" className="hover:text-white">
                หน้าแรก
              </button>
              <button type="button" className="hover:text-white">
                เกี่ยวกับ
              </button>
              <button type="button" className="hover:text-white">
                ติดต่อสอบถาม
              </button>
            </div>

            {/* <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15 active:scale-[0.99]"
                            aria-label="close"
                        >
                            ✕
                        </button> */}
          </div>
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center px-6">
        <div className="w-full max-w-6xl text-center">
          <div className="text-white font-medium leading-[0.95] text-[44px] sm:text-[64px] md:text-[88px]">
            Welcome to
            <br />
            <div className="mt-6">BestPhone</div>
          </div>

          <div className="mt-4 text-white/90 text-md">
            เบสท์โฟนเชียงใหม่ - รับซื้อ รับฝาก ผ่อน IPHONE IPAD สินค้า Apple
            ราคาดี เชียงใหม่
          </div>

          <div className="mt-8 mx-auto w-5/6 max-w-md px-5 py-6">
            <div className="flex flex-col sm:flex-col md:flex-row items-center justify-center gap-5">
              <button
                type="button"
                onClick={onEstimate}
                className="w-full sm:w-[280px] rounded-2xl bg-white px-6 py-4 text-center shadow-lg hover:shadow-xl active:scale-[0.99]"
              >
                <div className="inline-block text-left">
                  <div className="text-[#047DED] font-semibold">
                    ประเมินราคา
                  </div>
                  <div className="text-slate-900 font-bold mt-1">
                    รับซื้อ - ฝาก - ซ่อม
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={onMap}
                className="w-full sm:w-[280px] rounded-2xl bg-white px-6 py-4 text-center shadow-lg hover:shadow-xl active:scale-[0.99]"
              >
                <div className="inline-block text-left">
                  <div className="text-[#047DED] font-semibold leading-tight">
                    ที่ตั้งร้าน
                  </div>
                  <div className="text-slate-900 font-bold leading-tight mt-1">
                    Google map
                  </div>
                </div>
              </button>
            </div>

            <button
              type="button"
              onClick={onEnter}
              className="mt-5 w-full max-w-[580px] mx-auto block rounded-2xl bg-[#047DED] py-4 text-white font-semibold hover:bg-[#3EA2FE] active:scale-[0.99]"
            >
              เข้าสู่เว็บไซต์
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sliderImages = useMemo(() => [isMobile ? BG2 : BG1], [isMobile]);

  const categories = useMemo(
    () => [
      { id: "iphone", title: "iPhone", subtitle: "ไอโฟน" },
      { id: "ipad", title: "iPad", subtitle: "ไอแพด" },
      { id: "gadget", title: "Gadget", subtitle: "อุปกรณ์เสริม" },
      { id: "macbook", title: "Macbook", subtitle: "แล็ปท็อป" },
      { id: "apple_watch", title: "Apple Watch", subtitle: "นาฬิกา" },
    ],
    [],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [openHeroModal, setOpenHeroModal] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.sessionStorage.getItem(HERO_MODAL_SEEN_SESSION_KEY) !== "1";
  });

  useEffect(() => {
    if (!openHeroModal) return;
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(HERO_MODAL_SEEN_SESSION_KEY, "1");
  }, [openHeroModal]);

  const [page, setPage] = useState(1);
  const pageSize = 8;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [productCondition, setProductCondition] = useState("all");
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [installmentOnly, setInstallmentOnly] = useState(false);
  const [priceSort, setPriceSort] = useState("all");

  const [selectedCats, setSelectedCats] = React.useState([""]);

  const detectCategoryKey = useMemo(
    () => (product) => {
      const raw = product?.raw || {};
      const source = String(
        raw?.model?.category?.name || raw?.product_name || product?.title || "",
      )
        .trim()
        .toLowerCase();

      if (source.includes("iphone")) return "iphone";
      if (source.includes("ipad")) return "ipad";
      if (source.includes("macbook") || source.includes("mac"))
        return "macbook";
      if (source.includes("apple_watch") || source.includes("apple watch"))
        return "apple_watch";
      return "gadget";
    },
    [],
  );

  const normalizeText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const parseSearchCategory = (value) => {
    const [kind = "", id = ""] = String(value || "").split(":");
    return { kind, id };
  };

  const filteredRows = useMemo(() => {
    const gridCategory = normalizeText(selectedCats?.[0]);
    const keyword = normalizeText(searchQuery);
    const { kind, id } = parseSearchCategory(searchCategory);

    let next = rows.filter((item) => {
      const raw = item?.raw || {};

      if (keyword) {
        const haystack = [
          item?.title,
          item?.detail,
          item?.color,
          item?.capacity,
          raw?.product_name,
          raw?.model?.name,
          raw?.model?.category?.name,
          raw?.category?.name,
          raw?.accessory?.name,
        ]
          .map(normalizeText)
          .filter(Boolean)
          .join(" ");

        if (!haystack.includes(keyword)) return false;
      }

      if (gridCategory && gridCategory !== detectCategoryKey(item))
        return false;

      if (kind === "category" && id) {
        const candidateIds = [
          raw?.model?.category?.id != null ? String(raw.model.category.id) : "",
          raw?.model?.category?.documentId,
          raw?.category?.id != null ? String(raw.category.id) : "",
          raw?.category?.documentId,
        ].filter(Boolean);
        if (!candidateIds.includes(String(id))) return false;
      }

      if (kind === "accessory" && id) {
        const candidateIds = [
          raw?.accessory?.id != null ? String(raw.accessory.id) : "",
          raw?.accessory?.documentId,
        ].filter(Boolean);
        if (!candidateIds.includes(String(id))) return false;
      }

      if (modelFilter) {
        const rawModelId = String(
          raw?.model?.id ?? raw?.model?.documentId ?? "",
        ).trim();
        if (!rawModelId || rawModelId !== String(modelFilter)) return false;
      }

      if (productCondition === "new") {
        if (String(raw?.usage || "").trim() !== "new") return false;
      }
      if (productCondition === "used") {
        if (String(raw?.usage || "").trim() !== "used") return false;
      }
      if (productCondition === "warranty") {
        const w = String(raw?.warranty_info || "");
        if (!w.includes("ประกันศูนย์") && !w.includes("1 ปี")) return false;
      }

      if (onSaleOnly && !Boolean(raw?.is_onsale)) return false;

      if (installmentOnly) {
        const plan = String(raw?.installment_plan || "").trim();
        if (!plan) return false;
      }

      return true;
    });

    if (priceSort !== "all") {
      next = [...next].sort((a, b) => {
        const ap = Number(a?.price || 0);
        const bp = Number(b?.price || 0);
        return priceSort === "price_asc" ? ap - bp : bp - ap;
      });
    }

    return next;
  }, [
    rows,
    selectedCats,
    detectCategoryKey,
    searchQuery,
    searchCategory,
    modelFilter,
    productCondition,
    onSaleOnly,
    installmentOnly,
    priceSort,
  ]);

  const conditionOptions = useMemo(
    () => [
      { value: "all", label: "ตัวกรอง: ทั้งหมด" },
      { value: "new", label: "ตัวกรอง: สินค้ามือ 1" },
      { value: "used", label: "ตัวกรอง: สินค้ามือ 2" },
      { value: "warranty", label: "ตัวกรอง: สินค้าประกันเหลือ" },
    ],
    [],
  );

  const sortOptions = useMemo(
    () => [
      { value: "all", label: "เรียงลำดับ: ทั้งหมด" },
      { value: "price_desc", label: "เรียงลำดับ: ราคาสูง-ต่ำ" },
      { value: "price_asc", label: "เรียงลำดับ: ราคาต่ำ-สูง" },
    ],
    [],
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedCats,
    searchQuery,
    searchCategory,
    modelFilter,
    productCondition,
    onSaleOnly,
    installmentOnly,
    priceSort,
  ]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setLoadError("");

        const res = await api.get("products", {
          "filters[product_status][$eq]": "available",
          "pagination[page]": 1,
          "pagination[pageSize]": 1000,
          sort: "createdAt:desc",
        });

        if (ignore) return;

        const list = res?.data?.data || [];
        setRows((Array.isArray(list) ? list : []).map(mapProductToCardItem));
      } catch (e) {
        console.error(e);
        if (!ignore) {
          setRows([]);
          setLoadError("โหลดข้อมูลไม่สำเร็จ");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  // useEffect(() => {
  //     setPage(1);
  // }, [searchQuery, searchCategory, selectedCats]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div>
      <FullscreenHeroModal
        open={openHeroModal}
        onClose={() => setOpenHeroModal(false)}
        onEstimate={() => {
          // TODO: ใส่ route ที่คุณใช้จริง
          // navigate("/estimate");
          setOpenHeroModal(false);
        }}
        onMap={() => {
          // TODO: ใส่ลิงก์ map จริง
          window.open("https://maps.app.goo.gl/vpwoTXa7R4ZMtjvU8", "_blank");
        }}
        onEnter={() => {
          setOpenHeroModal(false);
        }}
      />
      <section className="w-full bg-[#F7F7F7]">
        <div className="w-full">
          <div className="relative w-full sm:h-[50vh] md:h-[90vh]">
            {/* <div className="grid grid-cols-12 gap-8 items-center justify-center h-full relative max-w-6xl mx-auto px-6 z-20">
                            <div className="col-span-12 md:col-span-6 text-white z-20 relative">
                                <div className="text-3xl font-medium opacity-90 mb-2">
                                    เบสท์โฟนเชียงใหม่
                                </div>
                                <div className="text-5xl md:text-8xl font-semibold leading-tight">
                                    BestPhone
                                </div>
                                <div className="text-2xl mt-4 text-white font-light">
                                    รับซื้อ ขาย ผ่อน ฝาก เทิร์น ไอโฟน ไอแพด
                                </div>
                            </div>
                        </div> */}
            <div className="absolute w-full h-full top-0 left-0">
              <HeroSlider images={sliderImages} />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 -mt-10 relative z-10">
        <SearchCard
          value={{ q: searchQuery, cat: searchCategory }}
          onQueryChange={setSearchQuery}
          onCategoryChange={setSearchCategory}
          modelValue={modelFilter}
          onModelChange={setModelFilter}
          productPlaceholder="เลือกสินค้า"
          modelPlaceholder="เลือกรุ่น"
          onSearch={() => setPage(1)}
        />
      </div>

      <section className="mx-auto max-w-6xl px-6 pt-10 pb-4">
        <CategoryGrid
          items={categories}
          value={selectedCats}
          onChange={setSelectedCats}
          onItemClick={(name) =>
            navigate(`/products?category=${encodeURIComponent(name || "")}`)
          }
        />
      </section>

      <section className="mx-auto max-w-6xl px-6 sm:py-2 md:py-10">
        <div className="text-center mb-6 items-center justify-center flex flex-col">
          <Star width={50} height={50} />
          <div className="sm:text-xs md:text-md text-[#047DED] font-semibold">
            Our Products
          </div>
          <div className="sm:text-xl md:text-3xl font-medium text-slate-900 mt-2">
            สินค้าของเรา
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="w-full md:w-[240px]">
              <SelectField
                icon={ListFilter}
                options={conditionOptions}
                value={productCondition}
                onChange={(v) => setProductCondition(String(v || "all"))}
                placeholder="ตัวกรอง: ทั้งหมด"
                buttonClassName="h-11 rounded-xl bg-white border border-slate-200"
                menuClassName="z-[8000]"
              />
            </div>

            <label className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={onSaleOnly}
                onChange={(e) => setOnSaleOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#047DED] focus:ring-[#047DED]"
              />
              <span>สินค้ากำลังลดราคา⚡</span>
            </label>

            <label className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={installmentOnly}
                onChange={(e) => setInstallmentOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#047DED] focus:ring-[#047DED]"
              />
              <span>มีผ่อนชำระ</span>
            </label>
          </div>

          <div className="w-full md:w-[260px] md:ml-auto">
            <SelectField
              icon={ListFilter}
              options={sortOptions}
              value={priceSort}
              onChange={(v) => setPriceSort(String(v || "all"))}
              placeholder="เรียงลำดับ: ทั้งหมด"
              buttonClassName="h-11 rounded-xl bg-white border border-slate-200"
              menuClassName="z-[8000]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {pageItems.map((p) => (
            <ProductCard key={p.id} item={p} />
          ))}
        </div>

        {!loading && !loadError && pageItems.length === 0 && (
          <div className="mt-6 text-center text-sm font-semibold text-slate-500">
            ไม่พบสินค้าที่ตรงกับตัวกรอง
          </div>
        )}

        {loadError && (
          <div className="mt-6 text-center text-sm font-semibold text-rose-600">
            {loadError}
          </div>
        )}

        {loading && (
          <div className="mt-6 text-center text-sm font-semibold text-slate-500">
            กำลังโหลด...
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </section>
      <div className="h-20" />
    </div>
  );
}
